import type { CareerCard, CareerDetail } from "@/hooks/useCareers";

export interface CareerFaq {
  question: string;
  answer: string;
}

function describeRole(title: string, description: string) {
  const text = description.trim();
  if (!text) return "";
  if (text.toLowerCase().startsWith(title.toLowerCase())) return text;
  return `${title} ${text[0].toLowerCase()}${text.slice(1)}`;
}

function sentenceFragment(value: string) {
  return value.trim().replace(/[.!?]+$/, "");
}

export function buildCareerFaqs(
  card: Pick<CareerCard, "title" | "description" | "education_pathway" | "salary_range">,
  detail?: Pick<CareerDetail, "work_environment" | "schedule_type" | "is_for_you"> | null,
): CareerFaq[] {
  const faqs: CareerFaq[] = [];
  const roleWithArticle = `${/^[aeiou]/i.test(card.title) ? "an" : "a"} ${card.title}`;
  const roleDescription = describeRole(card.title, card.description);
  if (roleDescription) {
    faqs.push({ question: `What does ${roleWithArticle} do?`, answer: roleDescription });
  }

  if (card.education_pathway.length > 0) {
    faqs.push({
      question: `How do you become ${roleWithArticle} in Canada?`,
      answer: `The pathway shown in this profile is ${card.education_pathway.join(" → ")}. Requirements vary by employer, province or territory, and the responsibilities of the position.`,
    });
  }

  const salaryAnswer = card.salary_range
    ? `This profile lists a broad estimate of ${card.salary_range}, not verified entry, mid-career, or senior wage bands. Check current regional Job Bank data and actual postings; compensation varies by employer, location, experience, schedule, and date.`
    : "";
  if (salaryAnswer) {
    faqs.push({ question: `How much does ${roleWithArticle} earn in Canada?`, answer: salaryAnswer });
  }

  const workParts = [
    detail?.work_environment ? `Typical work environment: ${sentenceFragment(detail.work_environment)}` : "",
    detail?.schedule_type ? `Typical schedule: ${sentenceFragment(detail.schedule_type)}` : "",
  ].filter(Boolean);
  if (workParts.length > 0) {
    faqs.push({
      question: `What is the work environment and schedule for ${roleWithArticle}?`,
      answer: `${workParts.join(". ")}. Conditions vary by employer, project, location, and whether the role is site-based, residential, or fly-in/fly-out.`,
    });
  }

  if ((detail?.is_for_you.length ?? 0) > 0) {
    faqs.push({
      question: `Who may be a good fit for a career as ${roleWithArticle}?`,
      answer: `This profile highlights these fit signals: ${detail!.is_for_you.join("; ")}. Use them as prompts for comparison, not as formal hiring criteria.`,
    });
  }

  return faqs;
}
